import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  CreditCard, 
  CheckCircle, 
  Clock, 
  Search,
  Filter,
  Building2,
  Calendar,
  Loader2,
  AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import SuperAdminLayout from '@/components/superadmin/SuperAdminLayout';
import { extractErrorMessage } from '@/utils/errorHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuperAdminPayments = () => {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [confirmingId, setConfirmingId] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Filters
  const [methodFilter, setMethodFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchOrders();
  }, [token]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data.orders || []);
    } catch (err) {
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatAmount = (amount) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleConfirmPayment = async (orderId) => {
    if (!window.confirm('Confirm this bank transfer payment?')) return;
    
    setConfirmingId(orderId);
    setError('');
    try {
      await axios.put(`${API}/payments/${orderId}/confirm`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess('Payment confirmed successfully');
      fetchOrders();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to confirm payment'));
    } finally {
      setConfirmingId(null);
    }
  };

  // Filter orders based on tab and filters
  const pendingBankTransfers = orders.filter(
    o => o.payment_method === 'bank_transfer' && o.status === 'payment_pending'
  );

  const allTransactions = orders.filter(order => {
    const matchesMethod = methodFilter === 'all' || order.payment_method === methodFilter;
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesMethod && matchesStatus;
  });

  const tabs = [
    { id: 'pending', label: 'Pending Bank Transfers', count: pendingBankTransfers.length },
    { id: 'all', label: 'All Transactions', count: orders.length }
  ];

  if (loading) {
    return (
      <SuperAdminLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-[#00E87A] animate-spin" />
        </div>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout>
      {/* Success Toast */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 bg-[#00E87A] text-black rounded-lg shadow-lg">
          <CheckCircle className="w-5 h-5" />
          {successMessage}
        </div>
      )}

      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">Payments</h1>
        <p className="text-gray-400 text-sm mt-1">Manage payment confirmations and view transaction history</p>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-gray-800">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`pb-3 px-1 text-sm font-medium transition-colors relative ${
              activeTab === tab.id 
                ? 'text-[#00E87A]' 
                : 'text-gray-400 hover:text-white'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                activeTab === tab.id ? 'bg-[#00E87A]/20' : 'bg-gray-800'
              }`}>
                {tab.count}
              </span>
            )}
            {activeTab === tab.id && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#00E87A]" />
            )}
          </button>
        ))}
      </div>

      {/* Pending Bank Transfers Tab */}
      {activeTab === 'pending' && (
        <div className="bg-[#161B22] border border-gray-800 rounded-lg overflow-hidden">
          {pendingBankTransfers.length === 0 ? (
            <div className="p-12 text-center">
              <CheckCircle className="w-12 h-12 text-[#00E87A] mx-auto mb-4" />
              <p className="text-white font-medium">All caught up!</p>
              <p className="text-gray-400 text-sm">No pending bank transfers to confirm</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-800">
                    <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Client</th>
                    <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3 hidden md:table-cell">Company</th>
                    <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Service</th>
                    <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Amount</th>
                    <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3 hidden md:table-cell">Date</th>
                    <th className="text-right text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {pendingBankTransfers.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-800/30 transition-colors">
                      <td className="px-4 py-4">
                        <span className="text-white text-sm">{order.user_email || '-'}</span>
                      </td>
                      <td className="px-4 py-4 hidden md:table-cell">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-gray-500" />
                          {order.company_id ? (
                            <Link 
                              to={`/superadmin/companies/${order.company_id}`}
                              className="text-[#00E87A] hover:text-[#00E87A]/80 text-sm transition-colors"
                            >
                              {order.company_name || '-'}
                            </Link>
                          ) : (
                            <span className="text-gray-300 text-sm">{order.company_name || '-'}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-gray-300 text-sm">{order.service_name || order.product_name || '-'}</span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-white text-sm font-semibold">
                          {formatAmount(order.final_amount || order.total_amount)}
                        </span>
                      </td>
                      <td className="px-4 py-4 hidden md:table-cell">
                        <div className="flex items-center gap-1 text-gray-400 text-sm">
                          <Calendar className="w-4 h-4" />
                          {formatDate(order.created_at)}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <button
                          onClick={() => handleConfirmPayment(order.id)}
                          disabled={confirmingId === order.id}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white disabled:opacity-50 transition-colors"
                          style={{ backgroundColor: '#00E87A' }}
                          data-testid={`confirm-payment-${order.id}`}
                        >
                          {confirmingId === order.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <CheckCircle className="w-4 h-4" />
                          )}
                          Confirm
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* All Transactions Tab */}
      {activeTab === 'all' && (
        <>
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-500" />
              <select
                value={methodFilter}
                onChange={(e) => setMethodFilter(e.target.value)}
                className="px-4 py-2.5 bg-[#161B22] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#00E87A]"
              >
                <option value="all">All Methods</option>
                <option value="card">Card (Stripe)</option>
                <option value="bank_transfer">Bank Transfer</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2.5 bg-[#161B22] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#00E87A]"
              >
                <option value="all">All Statuses</option>
                <option value="payment_pending">Pending</option>
                <option value="payment_received">Received</option>
                <option value="approved">Approved</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          </div>

          <div className="bg-[#161B22] border border-gray-800 rounded-lg overflow-hidden">
            {allTransactions.length === 0 ? (
              <div className="p-12 text-center">
                <CreditCard className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No transactions found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Client</th>
                      <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3 hidden md:table-cell">Service</th>
                      <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Amount</th>
                      <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Method</th>
                      <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Status</th>
                      <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3 hidden md:table-cell">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {allTransactions.map((order) => (
                      <tr key={order.id} className="hover:bg-gray-800/30 transition-colors">
                        <td className="px-4 py-4">
                          <span className="text-white text-sm">{order.user_email || '-'}</span>
                        </td>
                        <td className="px-4 py-4 hidden md:table-cell">
                          <span className="text-gray-300 text-sm">{order.service_name || order.product_name || '-'}</span>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-white text-sm font-semibold">
                            {formatAmount(order.final_amount || order.total_amount)}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium ${
                            order.payment_method === 'card' 
                              ? 'bg-blue-500/20 text-blue-400' 
                              : 'bg-purple-500/20 text-purple-400'
                          }`}>
                            <CreditCard className="w-3 h-3" />
                            {order.payment_method === 'card' ? 'Stripe' : 'Bank'}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                            order.status === 'payment_pending' ? 'bg-yellow-500/20 text-yellow-400' :
                            order.status === 'completed' || order.status === 'approved' ? 'bg-[#00E87A]/20 text-[#00E87A]' :
                            'bg-gray-600/20 text-gray-400'
                          }`}>
                            {order.status === 'payment_pending' && <Clock className="w-3 h-3" />}
                            {(order.status === 'completed' || order.status === 'approved') && <CheckCircle className="w-3 h-3" />}
                            {order.status?.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-4 hidden md:table-cell">
                          <span className="text-gray-400 text-sm">{formatDate(order.created_at)}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </SuperAdminLayout>
  );
};

export default SuperAdminPayments;
